import { classMap } from 'lit/directives/class-map.js';
import { HasSlotController } from '../../common/slot-controller.js';
import { html, literal } from 'lit/static-html.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { property, query, state } from 'lit/decorators.js';
import componentStyles from '../../styles/component.styles.js';
import styles from './button.styles.js';
import WebModuleElement from '../../common/webmodule-element.js';
import type { CSSResultGroup } from 'lit';

export type WebmoduleButtonVariant = 'default' | 'primary' | 'success' | 'neutral' | 'warning' | 'danger' | 'text';
export type WebmoduleButtonSize = 'small' | 'medium' | 'large';
/**
 *
 * @event webmodule-blur - Emitted when the button loses focus.
 * @event webmodule-focus - Emitted when the button gains focus.
 *
 * @slot - The button's label.
 * @slot prefix - A presentational prefix icon or similar element.
 * @slot suffix - A presentational suffix icon or similar element.
 *
 * @csspart base - The component's base wrapper.
 * @csspart prefix - The container that wraps the prefix.
 * @csspart label - The button's label.
 * @csspart suffix - The container that wraps the suffix.
 * @csspart caret - The button's caret icon, an `<webmodule-icon>` element.
 * @csspart spinner - The spinner that shows when the button is in the loading state.
 *
 * @tag webmodule-button
 */
export default class WebmoduleButton extends WebModuleElement {
  static styles: CSSResultGroup = [componentStyles, styles];

  @query('.button') button: HTMLButtonElement | HTMLLinkElement;
  @property() title = '';
  /** The button's theme variant. */
  @property({ reflect: true }) variant: WebmoduleButtonVariant = 'default';
  /** The button's size. */
  @property({ reflect: true }) size: WebmoduleButtonSize = 'medium';
  /** Draws the button with a caret. Used to indicate that the button triggers a dropdown menu or similar behavior. */
  @property({ type: Boolean, reflect: true }) caret = false;
  /** Draws the button with a caret up. Used to indicate that the button triggers a dropdown (dropup) menu or similar behavior. */
  @property({ attribute: 'caretup', type: Boolean, reflect: true }) caretUp = false;
  /** Disables the button. */
  @property({ type: Boolean, reflect: true }) disabled = false;
  /** Draws the button in a loading state. */
  @property({ type: Boolean, reflect: true }) loading = false;
  /** Draws an outlined button. */
  @property({ type: Boolean, reflect: true }) outline = false;
  /** Draws a pill-style button with rounded edges. */
  @property({ type: Boolean, reflect: true }) pill = false;
  /**
   * Draws a circular icon button. When this attribute is present, the button expects a single `<webmodule-icon>` in the
   * default slot.
   */
  @property({ type: Boolean, reflect: true }) circle = false;
  /**
   * The type of button. Note that the default value is `button` instead of `submit`, which is opposite of how native
   * `<button>` elements behave. When the type is `submit`, the button will submit the surrounding form.
   */
  @property() type: 'button' | 'submit' | 'reset' = 'button';
  /**
   * The name of the button, submitted as a name/value pair with form data, but only when this button is the submitter.
   * This attribute is ignored when `href` is present.
   */
  @property() name = '';
  /**
   * The value of the button, submitted as a pair with the button's name as part of the form data, but only when this
   * button is the submitter. This attribute is ignored when `href` is present.
   */
  @property() value = '';
  /** When set, the underlying button will be rendered as an `<a>` with this `href` instead of a `<button>`. */
  @property() href = '';
  /** Tells the browser where to open the link. Only used when `href` is present. */
  @property() target: '_blank' | '_parent' | '_self' | '_top';
  /**
   * When using `href`, this attribute will map to the underlying link's `rel` attribute. Unlike regular links, the
   * default is `noreferrer noopener` to prevent security exploits. However, if you're using `target` to point to a
   * specific tab/window, this will prevent that from working correctly. You can remove or change the default value by
   * setting the attribute to an empty string or a value of your choice, respectively.
   */
  @property() rel = 'noreferrer noopener';
  /** Tells the browser to download the linked file as this filename. Only used when `href` is present. */
  @property() downloadName?: string;

  private readonly hasSlotController = new HasSlotController(this, '[default]', 'prefix', 'suffix');
  @state() private hasFocus = false;

  click() {
    this.button.click();
  }

  focus(options?: FocusOptions) {
    this.button.focus(options);
  }

  blur() {
    this.button.blur();
  }

  render() {
    const isLink = this.isLink();
    const tag = isLink ? literal`a` : literal`button`;

    /* eslint-disable lit/no-invalid-html */
    /* eslint-disable lit/binding-positions */
    return html`
      <${tag}
        part="base"
        class=${classMap({
          button: true,
          'button--default': this.variant === 'default',
          'button--primary': this.variant === 'primary',
          'button--success': this.variant === 'success',
          'button--neutral': this.variant === 'neutral',
          'button--warning': this.variant === 'warning',
          'button--danger': this.variant === 'danger',
          'button--text': this.variant === 'text',
          'button--small': this.size === 'small',
          'button--medium': this.size === 'medium',
          'button--large': this.size === 'large',
          'button--caret': this.caret,
          'button--circle': this.circle,
          'button--disabled': this.disabled,
          'button--focused': this.hasFocus,
          'button--loading': this.loading,
          'button--standard': !this.outline,
          'button--outline': this.outline,
          'button--pill': this.pill,
          'button--has-label': this.hasSlotController.checkFor('[default]'),
          'button--has-prefix': this.hasSlotController.checkFor('prefix'),
          'button--has-suffix': this.hasSlotController.checkFor('suffix')
        })}
        ?disabled=${ifDefined(isLink ? undefined : this.disabled)}
        type=${ifDefined(isLink ? undefined : this.type)}
        title=${this.title}
        name=${ifDefined(isLink ? undefined : this.name)}
        value=${ifDefined(isLink ? undefined : this.value)}
        href=${ifDefined(isLink ? this.href : undefined)}
        target=${ifDefined(isLink ? this.target : undefined)}
        download=${ifDefined(isLink ? this.downloadName : undefined)}
        rel=${ifDefined(isLink ? this.rel : undefined)}
        role=${ifDefined(isLink ? undefined : 'button')}
        aria-disabled=${this.disabled ? 'true' : 'false'}
        tabindex=${this.disabled ? '-1' : '0'}
        @blur=${this.handleBlur}
        @focus=${this.handleFocus}
      >
        <slot name="prefix" part="prefix" class="button__prefix"></slot>
        <slot part="label" class="button__label"></slot>
        <slot name="suffix" part="suffix" class="button__suffix"></slot>
        ${
          this.caret
            ? html`
                <webmodule-icon
                  part="caret"
                  class="button__caret"
                  name="${this.caretUp ? 'caret-up' : 'caret'}"
                ></webmodule-icon>
              `
            : ''
        }
        ${this.loading ? html` <webmodule-spinner part="spinner"></webmodule-spinner>` : ''}
      </${tag}>
    `;
    /* eslint-enable lit/no-invalid-html */
    /* eslint-enable lit/binding-positions */
  }

  private handleBlur() {
    this.hasFocus = false;
    this.emit('webmodule-blur');
  }

  private handleFocus() {
    this.hasFocus = true;
    this.emit('webmodule-focus');
  }

  private isLink() {
    return this.href ? true : false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'webmodule-button': WebmoduleButton;
  }
}
